<template>
  <div>
    <div class="mt-6 flex flex-col">
      <div
        class="w-full md:max-w-4xl md:mx-auto px-4 flex flex-wrap mb-10 md:pb-20 md:pt-16"
      >
        <!--  For guests - we show basic features  -->
        <template v-if="!authenticated">
          <h1
            id="pricing-title"
            class="sm:text-5xl"
          >
            All of our core features are <span class="text-nt-blue">free</span>.
            You don't need to pay to create an awesome form.
          </h1>
          <features
            class="py-16"
            :features-only="true"
          />
        </template>
        <div
          v-if="user"
          class="w-full mt-5"
        >
          <div v-if="!user.is_subscribed">
            <h2 class="text-center text-5xl">
              <span class="relative inline-block">
                <svg
                  viewBox="0 0 52 24"
                  fill="currentColor"
                  class="text-nt-blue-light absolute top-0 left-0 z-0 hidden w-32 -mt-8 -ml-20 text-blue-gray-100 lg:w-32 lg:-ml-28 lg:-mt-10 sm:block"
                >
                  <defs>
                    <pattern
                      id="27df4f81-c854-45de-942a-fe90f7a300f9"
                      x="0"
                      y="0"
                      width=".135"
                      height=".30"
                    >
                      <circle
                        cx="1"
                        cy="1"
                        r=".7"
                      />
                    </pattern>
                  </defs>
                  <rect
                    fill="url(#27df4f81-c854-45de-942a-fe90f7a300f9)"
                    width="52"
                    height="24"
                  />
                </svg>
                <span class="relative">Create</span>
              </span>
              more forms with our
              <span
                class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600"
              >
                Pro</span>
              plan
            </h2>
            <h4 class="text-center mt-5 text-xl leading-9">
              Personalize everything, remove our branding and create as many forms as needed.
            </h4>
          </div>

          <div
            v-else
            class="mb-4"
          >
            <h1 class="text-center">
              You're a Pro subscriber!
            </h1>
            <p class="text-center">
              Enjoy unlimited form creation, remove branding and enjoy advanced customization options.
            </p>
          </div>

          <paid-plans v-if="!user.is_subscribed" />
        </div>
        <!--   Subscribed users - show -->
        <div
          v-else
          class="w-full mt-5"
        >
          <h2 class="text-center text-5xl">
            <span class="relative inline-block">
              <svg
                viewBox="0 0 52 24"
                fill="currentColor"
                class="text-nt-blue-light absolute top-0 left-0 z-0 hidden w-32 -mt-8 -ml-20 text-blue-gray-100 lg:w-32 lg:-ml-28 lg:-mt-10 sm:block"
              >
                <defs>
                  <pattern
                    id="27df4f81-c854-45de-942a-fe90f7a300f9"
                    x="0"
                    y="0"
                    width=".135"
                    height=".30"
                  >
                    <circle
                      cx="1"
                      cy="1"
                      r=".7"
                    />
                  </pattern>
                </defs>
                <rect
                  fill="url(#27df4f81-c854-45de-942a-fe90f7a300f9)"
                  width="52"
                  height="24"
                />
              </svg>
              <span class="relative">Create</span>
            </span>
            more forms with AirFormTable
            <span
              v-if="!user || !user.is_subscribed"
              class="text-nt-blue"
            >Pro</span>
          </h2>
          <h4 class="text-center mt-6 text-lg">
            Personalize everything, remove our branding and create as many forms as needed.
          </h4>
        </div>
        <template v-if="!authenticated">
          <pricing-table />
          <custom-plan />
          <div class="w-full mt-16 text-center">
            <h2>
              Create your first Form <span class="text-nt-blue">now</span>
            </h2>
            <h4 class="my-5 text-xl">
              AirFormTable is the best and easiest solution to create forms for
              AirTable users. You don't need take our word for it, just try for
              free!
            </h4>
            <v-button :to="{ name: 'register' }">
              Create a Form
            </v-button>
          </div>
        </template>
        <div
          v-else
          class="w-full"
        >
          <h2 class="mt-16 mb-5 text-3xl text-center">
            Our features
          </h2>
          <p class="">
            <span class="font-semibold">Free Plan</span>: Create one form with almost all of our features and
            AirFormTable branding.<br>
            <span class="font-semibold">Pro Plan</span>: Unlock unlimited form creation, remove AirFormTable branding for a
            professional look, and enjoy advanced customization options to perfectly match your brand.
          </p>
          <pricing-table />
        </div>
      </div>
      <airtable-forms-footer />
    </div>
  </div>
</template>

<script>
import {computed} from "vue"

export default {
  layout: "default",

  props: {},

  setup() {
    useAirtableFormsSeoMeta({
      title: "Pricing",
      description:
        "All of our core features are free, and there is no quantity limit. You can also created more advanced and customized forms with AirFormTable Pro.",
    })
    const authStore = useAuthStore()
    return {
      authenticated: computed(() => authStore.check),
      user: computed(() => authStore.user),
    }
  },

  data: () => ({
    checkoutLoading: false,
    billingLoading: false,
    monthly: true,
  }),

  computed: {
    motionOptions() {
      return {
        repeat: true, // infinite animation until stopped
        duration: 1000, // default duration. might be overridden by duration-{value} Tailwind class,
        ease: "linear",
      }
    },
  },

  mounted() {
  },
  methods: {
    openBilling() {
      this.billingLoading = true
      airtableFormsFetch.get("/subscription/billing-portal").then((data) => {
        this.billingLoading = false
        const url = data.portal_url
        window.location = url
      })
    },
    openCheckout(plan) {
      this.checkoutLoading = true
      airtableFormsFetch
        .get(
          "/subscription/new/" +
          plan +
          "/" +
          (this.monthly ? "monthly_2024" : "yearly_2024") +
          "/checkout/with-trial",
        )
        .then((data) => {
          window.location = data.checkout_url
        })
        .catch((error) => {
          this.alertError(error.data.message)
        })
        .finally(() => {
          this.checkoutLoading = false
        })
    },
    contactUs() {
      window.$crisp.push(["do", "chat:show"])
      window.$crisp.push(["do", "chat:open"])
    },
  },
}
</script>

<style scoped lang="scss">
#pricing-title {
  line-height: 1.2 !important;
}
</style>
